import {useSpecialistApi} from "../../core/hooks/use-specialist-api";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {State, ViewStates} from "@common/components/view-states";
import {ViewState} from "@common/models/enums/viev-state.enum";
import SpecialistList from "../components/specialist-list";
import AppButton from "@common/components/app-button";
import {ReactComponent as PlusIcon} from "@assets/icons/plus.svg";
import SpecialistSearchbar from "../components/specialist-searchbar";
import EmptySpecialistList from "../components/empty-specialist-list";
import SpecialistFormModal from "../components/specialist-form-modal";
import SpecialistDeleteConfirmModal from "../components/specialist-delete-confirm-modal";
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import {SpecialistListResponse} from "../../core/models/interfaces/specialist-list-response.interface";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {LoadingKeys} from "../../core/models/enums/loading-keys.enum";
import {SpecialistSearchResultDto} from "../../core/models/interfaces/specialists-search-result-dto.interface";
import useModalWithData from "@common/hooks/use-modal-with-data";

const SpecialistsListView = () => {
    const {error, getSearchSpecialists} = useSpecialistApi();
    const {isLoading} = useLoading();
    const [specialists, setSpecialists] = useState<SpecialistSearchResultDto[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [currentFilter, setCurrentFilter] = useState("");
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const {showModal: showSpecialistFormModal} = useModalWithData(ModalIds.SPECIALIST_FORM);

    const LIMIT = 20;
    const hasMore = useMemo(() => specialists.length < totalCount, [specialists]);

    const loadInitialSpecialists = async (preserveFilter: boolean = false, filterArg?: string) => {
        try {
            const filterToUse = preserveFilter ? (filterArg ?? currentFilter) : "";
            if (!preserveFilter) {
                setCurrentFilter("");
            }

            const response = await getSearchSpecialists(filterToUse, LIMIT, 0);
            setSpecialists(response.specialists);
            setTotalCount(response.totalCount);
            setCurrentOffset(LIMIT);
        } catch (error) {
            console.error('Error loading specialists:', error);
        }
    };

    const handleCreateNew = (): void => {
        showSpecialistFormModal({
            specialistId: undefined,
            onSuccess: loadInitialSpecialists
        });
    };

    const handleSearchResults = (response: SpecialistListResponse, query: string) => {
        setSpecialists(response.specialists);
        setTotalCount(response.totalCount);
        setCurrentOffset(response.specialists.length);
        setCurrentFilter(query);
    };

    const handleQueryChange = (query: string) => {
        setCurrentFilter(query);
    }

    const loadMoreSpecialists = async () => {
        if (isLoadingMore || !hasMore) return;

        try {
            setIsLoadingMore(true);
            const response = await getSearchSpecialists(currentFilter, LIMIT, currentOffset);
            setSpecialists(prev => [...prev, ...response.specialists]);
            setCurrentOffset(prev => prev + LIMIT);
        } catch (error) {
            console.error('Error loading more specialists:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        void loadInitialSpecialists(false);

        const handleRefresh = () => {
            window.dispatchEvent(new CustomEvent('specialists:clear-filter'));
            setCurrentFilter("");
            void loadInitialSpecialists(false);
        };

        window.addEventListener('specialists:refresh', handleRefresh);
        return () => window.removeEventListener('specialists:refresh', handleRefresh);
    }, []);

    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        if (!hasMore) {
            return;
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadingMore && hasMore) {
                    loadMoreSpecialists();
                }
            },
            {threshold: 0.1}
        );

        if (loadMoreRef.current) {
            observerRef.current.observe(loadMoreRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isLoadingMore, currentOffset, currentFilter, hasMore]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Specjaliści</h2>
                <span className="font-semibold text-lg">
                    Specjaliści to miejsce, gdzie możesz dodać ekspertów medycznych i polecać ich w swoich artykułach. 
                    Twórz własną bazę specjalistów i łatwo udostępniaj ich pod swoimi treściami. 
                    Specjaliści również mogą być wybrani jako autorzy artykułu.
                </span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center">
                    <SpecialistSearchbar emitResults={handleSearchResults} onQueryChange={handleQueryChange}/>
                    <AppButton
                        onClick={handleCreateNew}
                        className="bg-primary hover:bg-primary/80 w-fit rounded-[4rem] h-[40px] min-w-fit">
                        <div className="flex gap-2 text-red-400 justify-center items-center whitespace-nowrap">
                            <span className="font-semibold text-base text-on-primary ">Nowy specjalista</span>
                            <PlusIcon className="text-on-primary"/>
                        </div>
                    </AppButton>
                </div>
            </div>
            <div className="pt-6">
                <ViewStates
                    loading={isLoading([LoadingKeys.USE_SPECIALIST_API, "getSearchSpecialists"]) && specialists.length === 0}
                    error={error}
                    data={specialists}>
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        <EmptySpecialistList/>
                    </State>
                    <State state={ViewState.FETCHED}>
                        <SpecialistList
                            specialists={specialists}
                            isLoadingMore={isLoadingMore}
                            onSuccessDelete={(id) => {
                                setSpecialists(prevState => prevState.filter(e => e.id !== id))
                            }}
                        />
                        {hasMore && <div ref={loadMoreRef} className="h-6"/>}
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>

            <SpecialistFormModal/>
            <SpecialistDeleteConfirmModal/>
        </>
    );
}

export default SpecialistsListView
